import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  syncFundingData,
  getBenefitApplications,
  getAllocations,
  getAllCreditCodes,
  updateApplicationProject
} from '../../../services/fundingApi';
import {
  getManualCredits,
  getManualCreditsSummary,
  createManualCredit,
  updateManualCredit,
  deleteManualCredit
} from '../../../services/cloudopsApi';
import * as realAPI from '../../../services/realApi';

interface Application {
  id: string;
  name?: string;
  activity_name?: string;
  program_name?: string;
  status: string;
  stage?: string;
  fulfillment_types?: string;
  budget_year?: string | number;
  approved_amount?: number;
  requested_amount?: number;
  value_per_code?: number;
  number_of_codes?: number;
  application_id?: string;
  business_description?: string;
  aws_account_id?: string;
  planned_start_date?: string;
  planned_end_date?: string;
  project_id?: string;
  project_name?: string;
  calculator_urls?: string;
  file_details?: string;
}

interface Allocation {
  id: string;
  name?: string;
  status?: string;
  allocation_amount?: number;
  issued_amount?: number;
  starts_at?: string;
  expiration_date?: string;
}

interface CreditCode {
  id: string;
  credit_code: string;
  amount?: number;
  status: string;
  aws_account_id?: string;
  matched_client_name?: string;
  expiration_date?: string;
}

interface ManualCredit {
  id: string;
  credit_name: string;
  total_amount: number;
  remaining_amount: number;
  start_date: string;
  end_date: string;
  credit_type: string;
  aws_account_id?: string;
  notes?: string;
  is_active?: boolean;
}

interface ManualCreditsSummary {
  total_credits?: number;
  remaining_credits?: number;
  consumed_credits?: number;
  active_count?: number;
}

interface AwsAccount {
  account_id: string;
  account_name: string;
  account_type?: string;
  environment_names?: string[];
}

interface CreditFormData {
  credit_name: string;
  total_amount: string;
  start_date: string;
  end_date: string;
  credit_type: string;
  aws_account_id: string;
  notes: string;
}

interface SyncResult {
  applications_synced?: number;
  allocations_synced?: number;
  credit_codes_synced?: number;
  matched_clients?: number;
}

interface Project {
  id: string;
  name: string;
  client_name?: string;
}

interface ProgressStep {
  label: string;
  status: string;
}

type ViewMode = 'applications' | 'credit-codes';
type TabMode = 'info' | 'project' | 'credits' | 'documents';

const FundingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const { hasPermission, isAdmin } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [creditCodes, setCreditCodes] = useState<CreditCode[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [selectedTab, setSelectedTab] = useState<TabMode>('info');
  const [view, setView] = useState<ViewMode>('applications');
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [updatingProject, setUpdatingProject] = useState(false);

  const [manualCredits, setManualCredits] = useState<ManualCredit[]>([]);
  const [manualCreditsSummary, setManualCreditsSummary] = useState<ManualCreditsSummary | null>(null);
  const [showAddCreditModal, setShowAddCreditModal] = useState(false);
  const [editingCredit, setEditingCredit] = useState<ManualCredit | null>(null);
  const [creditFormData, setCreditFormData] = useState<CreditFormData>({
    credit_name: '',
    total_amount: '',
    start_date: '',
    end_date: '',
    credit_type: 'custom',
    aws_account_id: '',
    notes: ''
  });
  const [awsAccounts, setAwsAccounts] = useState<AwsAccount[]>([]);

  const canAccessFunding =
    hasPermission(PERMISSIONS.FINANCE_VIEW) ||
    hasPermission(PERMISSIONS.FINANCE_EDIT) ||
    hasPermission(PERMISSIONS.FINANCE_MANAGE) ||
    isAdmin ||
    isAWSUser();

  useEffect(() => {
    if (canAccessFunding) {
      loadData();
      loadAwsAccounts();
      loadProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, canAccessFunding]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestId = params.get('request');

    if (requestId && applications.length > 0 && !selectedApplication) {
      const application = applications.find(app => app.id === requestId);
      if (application) {
        loadApplicationDetails(application);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applications, location.search]);

  const loadData = async () => {
    try {
      setLoading(true);
      const apps = await getBenefitApplications() as Application[];
      setApplications(apps ?? []);
      const codes = await getAllCreditCodes() as CreditCode[];
      setCreditCodes(codes ?? []);

      if (user?.client_id) {
        await loadManualCredits();
      }
    } catch (err: unknown) {
      console.error('Error loading funding data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadManualCredits = async () => {
    const clientId = selectedClientId ?? user?.client_id;
    if (!clientId) return;

    try {
      const credits = await getManualCredits({
        client_id: clientId,
        include_expired: true
      }) as ManualCredit[];
      setManualCredits(credits ?? []);

      const summary = await getManualCreditsSummary(clientId) as ManualCreditsSummary;
      setManualCreditsSummary(summary ?? null);
    } catch (err: unknown) {
      console.error('Error loading manual credits:', err);
    }
  };

  const loadAwsAccounts = async () => {
    try {
      const clientId = selectedClientId ?? user?.client_id;

      if (!clientId) {
        return;
      }

      const accountsResponse = await realAPI.awsEnvironmentsAPI.getAccounts(clientId);

      if (accountsResponse.success) {
        const accounts = accountsResponse.data ?? [];

        const accountsArray: AwsAccount[] = accounts.map((account: Record<string, unknown>) => ({
          account_id: account.account_id as string,
          account_name: (account.account_name as string) ?? (account.account_id as string),
          account_type: (account.account_type as string) ?? 'standard',
          environment_names: (account.environment_names as string[]) ?? [],
        }));

        setAwsAccounts(accountsArray);
      }
    } catch (err: unknown) {
      console.error('Error loading AWS accounts:', err);
    }
  };

  const loadProjects = async () => {
    try {
      const clientId = selectedClientId ?? user?.client_id;
      if (!clientId) return;

      const result = await realAPI.projectAPI.getProjects(user?.role ?? '', user?.partner_id ?? null, null, clientId);
      if (result.success) {
        setProjects(result.data ?? []);
      }
    } catch (err: unknown) {
      console.error('Error loading projects:', err);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await syncFundingData() as SyncResult;
      setLastSyncResult(result);
      await loadData();
      setTimeout(() => setLastSyncResult(null), 5000);
    } catch (err: unknown) {
      console.error('Error syncing funding data:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to sync funding data: ' + message);
    } finally {
      setSyncing(false);
    }
  };

  const loadApplicationDetails = async (application: Application) => {
    try {
      setSelectedApplication(application);
      setSelectedTab('info');
      const allocs = await getAllocations(application.id) as Allocation[];
      setAllocations(allocs ?? []);

      const params = new URLSearchParams(location.search);
      params.set('request', application.id);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    } catch (err: unknown) {
      console.error('Error loading allocations:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to load allocations: ' + message);
    }
  };

  const handleProjectAssociation = async (projectId: string) => {
    if (!selectedApplication) return;

    try {
      setUpdatingProject(true);
      const updatedApp = await updateApplicationProject(selectedApplication.id, projectId) as Application;

      setApplications(prev => prev.map(app =>
        app.id === selectedApplication.id ? updatedApp : app
      ));

      setSelectedApplication(updatedApp);
    } catch (err: unknown) {
      console.error('Error updating project association:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to associate with project: ' + message);
    } finally {
      setUpdatingProject(false);
    }
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadgeColor = (status: string): string => {
    const statusColors: Record<string, string> = {
      'APPROVED': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'PENDING_SUBMISSION': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      'SUBMITTED': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'REJECTED': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'FULFILLED': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Active': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'Redeemed': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'Expired': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    };
    return statusColors[status] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  const getProgressSteps = (app: Application): ProgressStep[] => {
    const status = app.status;
    const stage = app.stage;

    return [
      { label: 'Application Creation', status: 'Created' },
      { label: 'Business Approval', status: status === 'APPROVED' || stage === 'FULFILLMENT' ? 'Approved' : 'Pending' },
      { label: 'Finance Approval', status: status === 'APPROVED' || stage === 'FULFILLMENT' ? 'Approved' : 'Pending' },
      { label: 'Pre-approval', status: status === 'APPROVED' || stage === 'FULFILLMENT' ? 'Approved' : 'Pending' },
      { label: 'Application Completion', status: stage === 'FULFILLMENT' ? 'Completed' : 'Pending' }
    ];
  };

  const handleOpenAddCreditModal = () => {
    setEditingCredit(null);
    setCreditFormData({
      credit_name: '',
      total_amount: '',
      start_date: '',
      end_date: '',
      credit_type: 'custom',
      aws_account_id: '',
      notes: ''
    });
    setShowAddCreditModal(true);
  };

  const handleEditCredit = (credit: ManualCredit) => {
    setEditingCredit(credit);
    setCreditFormData({
      credit_name: credit.credit_name,
      total_amount: String(credit.total_amount),
      start_date: credit.start_date,
      end_date: credit.end_date,
      credit_type: credit.credit_type,
      aws_account_id: credit.aws_account_id ?? '',
      notes: credit.notes ?? ''
    });
    setShowAddCreditModal(true);
  };

  const handleSaveCredit = async () => {
    try {
      const clientId = selectedClientId ?? user?.client_id;
      if (!clientId) {
        alert('Client ID is required');
        return;
      }

      if (!creditFormData.aws_account_id) {
        alert('AWS Account is required');
        return;
      }

      const creditData = {
        credit_name: creditFormData.credit_name,
        total_amount: Number.parseFloat(creditFormData.total_amount),
        start_date: creditFormData.start_date,
        end_date: creditFormData.end_date,
        credit_type: creditFormData.credit_type,
        aws_account_id: creditFormData.aws_account_id,
        notes: creditFormData.notes || null
      };

      if (editingCredit) {
        await updateManualCredit(editingCredit.id, creditData, clientId);
      } else {
        await createManualCredit(creditData, clientId);
      }

      setShowAddCreditModal(false);
      await loadManualCredits();
    } catch (err: unknown) {
      console.error('Error saving manual credit:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to save credit: ' + message);
    }
  };

  const handleDeleteCredit = async (creditId: string) => {
    if (!globalThis.confirm('Are you sure you want to delete this credit? This action cannot be undone.')) {
      return;
    }

    try {
      const clientId = selectedClientId ?? user?.client_id;
      await deleteManualCredit(creditId, clientId ?? '');
      await loadManualCredits();
    } catch (err: unknown) {
      console.error('Error deleting manual credit:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Failed to delete credit: ' + message);
    }
  };

  const uniqueClients = Array.from(new Set(
    creditCodes.filter(c => c.matched_client_name).map(c => c.matched_client_name as string)
  )).sort((a, b) => a.localeCompare(b));

  const filteredCreditCodes = creditCodes.filter(code => {
    if (clientFilter === 'matched' && !code.matched_client_name) return false;
    if (clientFilter === 'unmatched' && code.matched_client_name) return false;
    if (
      clientFilter !== 'all' &&
      clientFilter !== 'matched' &&
      clientFilter !== 'unmatched' &&
      code.matched_client_name !== clientFilter
    ) return false;
    if (statusFilter !== 'all' && code.status !== statusFilter) return false;
    return true;
  });

  if (!canAccessFunding) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-800 dark:text-yellow-200">
            Funding information is only available to partners and AWS staff.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Funding &amp; Credits
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage AWS Partner Central funding requests and credit allocations
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {syncing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Syncing...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Sync from Partner Central
            </>
          )}
        </button>
      </div>

      {/* Sync Result Banner */}
      {lastSyncResult && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-green-800 dark:text-green-200 font-medium">
                Sync completed successfully!
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                {lastSyncResult.applications_synced} applications, {lastSyncResult.allocations_synced} allocations, {lastSyncResult.credit_codes_synced} credit codes synced.
                {(lastSyncResult.matched_clients ?? 0) > 0 && ` Matched ${lastSyncResult.matched_clients} credit codes to client accounts.`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setView('applications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              view === 'applications'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Funding Requests ({applications.length})
          </button>
          <button
            onClick={() => setView('credit-codes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              view === 'credit-codes'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Credits ({creditCodes.length + manualCredits.length})
            {view === 'credit-codes' && filteredCreditCodes.length < creditCodes.length && (
              <span className="ml-1 text-xs">({filteredCreditCodes.length} shown)</span>
            )}
          </button>
        </nav>
      </div>

      {/* Applications View */}
      {view === 'applications' && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-1 space-y-3">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">
              Fund Requests
            </h2>
            {applications.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center text-gray-500 dark:text-gray-400">
                <p>No funding requests found.</p>
                <p className="text-sm mt-2">Click &quot;Sync from Partner Central&quot; to fetch data.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {applications.map((app) => (
                  <div
                    key={app.id}
                    onClick={() => loadApplicationDetails(app)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') loadApplicationDetails(app); }} role="button" tabIndex={0}
                    className={`bg-white dark:bg-gray-800 rounded-lg p-4 cursor-pointer border-2 transition-colors ${
                      selectedApplication?.id === app.id
                        ? 'border-blue-500'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="space-y-2">
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        {app.activity_name ?? app.name ?? 'Unnamed Request'}
                      </h3>

                      {app.program_name && (
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {app.program_name}
                        </p>
                      )}

                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(app.status)}`}>
                          {app.status}
                        </span>
                        {app.fulfillment_types && (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                            {app.fulfillment_types}
                          </span>
                        )}
                        {app.budget_year && (
                          <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                            {app.budget_year}
                          </span>
                        )}
                      </div>

                      {app.approved_amount && (
                        <div className="text-lg font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(app.approved_amount)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Application Details */}
          <div className="lg:col-span-3">
            {selectedApplication ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                {/* Header */}
                <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {selectedApplication.activity_name ?? selectedApplication.name ?? 'Fund Request Details'}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Track your fund request through each approval stage
                  </p>
                </div>

                {/* Progress Indicator */}
                <div className="border-b border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Fund Request Progress</h3>
                  <div className="grid grid-cols-5 gap-2">
                    {getProgressSteps(selectedApplication).map((step, idx) => (
                      <div key={step.label} className="text-center">
                        <div className={`text-xs font-medium mb-2 ${
                          step.status === 'Pending' ? 'text-gray-400' : 'text-green-600 dark:text-green-400'
                        }`}>
                          {idx + 1}. {step.label}
                        </div>
                        <div className={`flex items-center justify-center w-full h-2 rounded ${
                          step.status === 'Pending' ? 'bg-gray-200 dark:bg-gray-700' : 'bg-green-500'
                        }`}>
                          {step.status !== 'Pending' && (
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <div className="text-xs font-medium mt-1 text-gray-600 dark:text-gray-400">
                          {step.status}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 dark:border-gray-700">
                  <nav className="-mb-px flex px-6">
                    {(['info', 'project', 'credits', 'documents'] as TabMode[]).map(tab => (
                      <button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        className={`py-4 px-4 border-b-2 font-medium text-sm ${
                          selectedTab === tab
                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400'
                        }`}
                      >
                        {tab === 'info' && 'Fund Request Information'}
                        {tab === 'project' && 'Project Details'}
                        {tab === 'credits' && 'Credit Request Details'}
                        {tab === 'documents' && 'Documents'}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {selectedTab === 'info' && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Fund Request ID</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{selectedApplication.application_id}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</span>
                          <p className="mt-1"><span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(selectedApplication.status)}`}>{selectedApplication.status}</span></p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Program</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedApplication.program_name ?? 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Activity Name</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedApplication.activity_name ?? 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Budget Year</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedApplication.budget_year ?? 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Fulfillment Type</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedApplication.fulfillment_types ?? 'N/A'}</p>
                        </div>
                      </div>

                      {selectedApplication.business_description && (
                        <div className="mt-6">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Business Description</span>
                          <p className="mt-2 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedApplication.business_description}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedTab === 'project' && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <label htmlFor="associated-hub-project" className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                          Associated Hub Project
                        </label>
                        <select
                          id="associated-hub-project"
                          value={selectedApplication.project_id ?? ''}
                          onChange={(e) => handleProjectAssociation(e.target.value || '')}
                          disabled={updatingProject}
                          className="w-full px-3 py-2 border border-blue-300 dark:border-blue-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="">-- Select Project --</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name} {project.client_name ? `(${project.client_name})` : ''}
                            </option>
                          ))}
                        </select>
                        {selectedApplication.project_name && (
                          <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
                            Currently linked to: <span className="font-medium">{selectedApplication.project_name}</span>
                          </p>
                        )}
                        <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                          Associate this funding request with a project in Hub to track funding per project
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">AWS Account ID</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{selectedApplication.aws_account_id ?? 'N/A'}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Project Start Date</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(selectedApplication.planned_start_date)}</p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Project End Date</span>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(selectedApplication.planned_end_date)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedTab === 'credits' && (
                    <div>
                      <div className="grid grid-cols-3 gap-6 mb-6">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Value per Credit Code (USD)</span>
                          <p className="mt-2 text-2xl font-bold text-blue-900 dark:text-blue-100">{formatCurrency(selectedApplication.value_per_code)}</p>
                        </div>
                        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Number of Codes</span>
                          <p className="mt-2 text-2xl font-bold text-purple-900 dark:text-purple-100">{selectedApplication.number_of_codes ?? 'N/A'}</p>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                          <span className="text-sm font-medium text-green-700 dark:text-green-300">Requested Credit Amount (USD)</span>
                          <p className="mt-2 text-2xl font-bold text-green-900 dark:text-green-100">{formatCurrency(selectedApplication.requested_amount)}</p>
                        </div>
                      </div>

                      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Credit Allocation Summary</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Allocated Amount (USD)</span>
                            <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                              {formatCurrency(allocations.reduce((sum, a) => sum + (a.allocation_amount ?? 0), 0))}
                            </p>
                          </div>
                          <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-lg">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Total Issued Amount (USD)</span>
                            <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">
                              {formatCurrency(allocations.reduce((sum, a) => sum + (a.issued_amount ?? 0), 0))}
                            </p>
                          </div>
                        </div>

                        {allocations.length > 0 && (
                          <div className="mt-6 space-y-3">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Allocations ({allocations.length})</h4>
                            {allocations.map((alloc) => (
                              <div key={alloc.id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <h5 className="font-medium text-gray-900 dark:text-white">{alloc.name ?? 'Credit Allocation'}</h5>
                                    {alloc.status && (
                                      <span className={`mt-1 inline-block px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(alloc.status)}`}>
                                        {alloc.status}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Allocated:</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatCurrency(alloc.allocation_amount)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Issued:</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatCurrency(alloc.issued_amount)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Starts:</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatDate(alloc.starts_at)}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-600 dark:text-gray-400">Expires:</span>
                                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{formatDate(alloc.expiration_date)}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedTab === 'documents' && (
                    <div className="space-y-4">
                      {selectedApplication.calculator_urls && (() => {
                        try {
                          const urls = JSON.parse(selectedApplication.calculator_urls) as string[];
                          if ((urls?.length ?? 0) > 0) {
                            return (
                              <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">AWS Pricing Calculator</h3>
                                <div className="space-y-2">
                                  {urls.map((url) => (
                                    <a
                                      key={url}
                                      href={url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      </svg>
                                      <span className="text-sm font-medium">View Pricing Calculator Estimate</span>
                                      <svg className="w-4 h-4 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                      </svg>
                                    </a>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                        } catch (_e) { console.error('Failed to parse calculator_urls', _e); }
                        return null;
                      })()}

                      {selectedApplication.file_details && (() => {
                        try {
                          const files = JSON.parse(selectedApplication.file_details) as Array<{
                            FileName?: string;
                            BusinessUseCase?: string;
                            FileURI?: string;
                          }>;
                          if ((files?.length ?? 0) > 0) {
                            return (
                              <div>
                                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Attached Documents</h3>
                                <div className="space-y-2">
                                  {files.map((file, idx) => (
                                    <div key={file.FileName || `file-${idx}`} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                      </svg>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.FileName ?? 'Document'}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{file.BusinessUseCase ?? 'Attachment'}</p>
                                      </div>
                                      {file.FileURI && (
                                        <a
                                          href={file.FileURI}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                                        >
                                          Download
                                        </a>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                        } catch (_e) { console.error('Failed to parse file_details', _e); }
                        return null;
                      })()}

                      {!selectedApplication.calculator_urls && !selectedApplication.file_details && (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p>No documents or calculator links available for this request</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-12 text-center text-gray-500 dark:text-gray-400">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium">Select a funding request to view details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Credit Codes View */}
      {view === 'credit-codes' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">All Credits</h2>
            <button
              onClick={handleOpenAddCreditModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Credits
            </button>
          </div>

          {/* Credits from Funding Requests */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Credits Granted from Funding Requests</h3>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="filter-by-client" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Client
                  </label>
                  <select
                    id="filter-by-client"
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">All Clients</option>
                    <option value="matched">Matched to Clients</option>
                    <option value="unmatched">Unmatched</option>
                    {uniqueClients.map(client => (
                      <option key={client} value={client}>{client}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="filter-by-status" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Filter by Status
                  </label>
                  <select
                    id="filter-by-status"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="all">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Redeemed">Redeemed</option>
                    <option value="Expired">Expired</option>
                    <option value="Revoked">Revoked</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing <span className="font-medium text-gray-900 dark:text-white">{filteredCreditCodes.length}</span> of <span className="font-medium text-gray-900 dark:text-white">{creditCodes.length}</span> credit codes
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credit Code</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">AWS Account</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Matched Client</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expiration</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredCreditCodes.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                          {creditCodes.length === 0
                            ? 'No credit codes found. Click "Sync from Partner Central" to fetch data.'
                            : 'No credit codes match the current filters.'}
                        </td>
                      </tr>
                    ) : (
                      filteredCreditCodes.map((code) => (
                        <tr key={code.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <code className="text-sm font-mono bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white px-2 py-1 rounded">
                              {code.credit_code}
                            </code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(code.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadgeColor(code.status)}`}>
                              {code.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {code.aws_account_id ?? 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {code.matched_client_name ? (
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="font-medium text-gray-900 dark:text-white">{code.matched_client_name}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Unmatched
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {formatDate(code.expiration_date)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Other Credits Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Other Credits</h3>

            {manualCreditsSummary && (manualCreditsSummary.active_count ?? 0) > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Credits</span>
                  <p className="mt-2 text-2xl font-bold text-blue-900 dark:text-blue-100">
                    {formatCurrency(manualCreditsSummary.total_credits)}
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <span className="text-sm font-medium text-green-700 dark:text-green-300">Remaining Credits</span>
                  <p className="mt-2 text-2xl font-bold text-green-900 dark:text-green-100">
                    {formatCurrency(manualCreditsSummary.remaining_credits)}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                  <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Consumed Credits</span>
                  <p className="mt-2 text-2xl font-bold text-orange-900 dark:text-orange-100">
                    {formatCurrency(manualCreditsSummary.consumed_credits)}
                  </p>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                  <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Active Credits</span>
                  <p className="mt-2 text-2xl font-bold text-purple-900 dark:text-purple-100">
                    {manualCreditsSummary.active_count}
                  </p>
                </div>
              </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
              {manualCredits.length === 0 ? (
                <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium mb-2">No other credits found</p>
                  <p className="text-sm">Click &quot;Add Credits&quot; above to create your first manual credit entry</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Credit Name</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total Amount</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Remaining</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Period</th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {manualCredits.map((credit) => {
                        const isExpired = new Date(credit.end_date) < new Date();
                        const consumedPercent = ((Number.parseFloat(String(credit.total_amount)) - Number.parseFloat(String(credit.remaining_amount))) / Number.parseFloat(String(credit.total_amount))) * 100;
                        let statusClass: string;
                        let statusLabel: string;
                        if (isExpired) {
                          statusClass = 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
                          statusLabel = 'Expired';
                        } else if (credit.is_active) {
                          statusClass = 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
                          statusLabel = 'Active';
                        } else {
                          statusClass = 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
                          statusLabel = 'Inactive';
                        }

                        return (
                          <tr key={credit.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-6 py-4">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {credit.credit_name}
                              </div>
                              {credit.notes && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {credit.notes}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                                {credit.credit_type}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {formatCurrency(credit.total_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {formatCurrency(credit.remaining_amount)}
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                                <div
                                  className="bg-green-600 h-1.5 rounded-full"
                                  style={{ width: `${100 - consumedPercent}%` }}
                                />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              <div>{formatDate(credit.start_date)}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">to {formatDate(credit.end_date)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleEditCredit(credit)}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCredit(credit.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Credit Modal */}
      {showAddCreditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl mx-4">
            <div className="border-b border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingCredit ? 'Edit Manual Credit' : 'Add Manual Credit'}
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="credit-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Credit Name *
                </label>
                <input
                  id="credit-name"
                  type="text"
                  value={creditFormData.credit_name}
                  onChange={(e) => setCreditFormData(prev => ({ ...prev, credit_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  placeholder="e.g., Q1 2025 AWS Credits"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="credit-total-amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Total Amount (USD) *
                  </label>
                  <input
                    id="credit-total-amount"
                    type="number"
                    step="0.01"
                    value={creditFormData.total_amount}
                    onChange={(e) => setCreditFormData(prev => ({ ...prev, total_amount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    placeholder="5000.00"
                  />
                </div>
                <div>
                  <label htmlFor="credit-type" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Credit Type
                  </label>
                  <select
                    id="credit-type"
                    value={creditFormData.credit_type}
                    onChange={(e) => setCreditFormData(prev => ({ ...prev, credit_type: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="custom">Custom</option>
                    <option value="promotional">Promotional</option>
                    <option value="enterprise">Enterprise Agreement</option>
                    <option value="partner">Partner Benefit</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="credit-start-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date *
                  </label>
                  <input
                    id="credit-start-date"
                    type="date"
                    value={creditFormData.start_date}
                    onChange={(e) => setCreditFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label htmlFor="credit-end-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date *
                  </label>
                  <input
                    id="credit-end-date"
                    type="date"
                    value={creditFormData.end_date}
                    onChange={(e) => setCreditFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="credit-aws-account" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AWS Account *
                </label>
                <select
                  id="credit-aws-account"
                  value={creditFormData.aws_account_id}
                  onChange={(e) => setCreditFormData(prev => ({ ...prev, aws_account_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  required
                >
                  <option value="">Select an AWS Account</option>
                  {awsAccounts.map((account) => {
                    const accountTypeLabel =
                      account.account_type === 'payer' ? 'Payer' :
                      account.account_type === 'partner' ? 'Partner' :
                      'Member';
                    const envNames = (account.environment_names?.length ?? 0) > 0
                      ? ` - Env: ${account.environment_names?.join(', ')}`
                      : '';
                    const displayName = account.account_name === account.account_id
                      ? account.account_id
                      : `${account.account_name} (${account.account_id})`;
                    return (
                      <option key={account.account_id} value={account.account_id}>
                        {displayName} - {accountTypeLabel}{envNames}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label htmlFor="credit-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Notes (optional)
                </label>
                <textarea
                  id="credit-notes"
                  value={creditFormData.notes}
                  onChange={(e) => setCreditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                  placeholder="Additional information about this credit..."
                />
              </div>
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3">
              <button
                onClick={() => setShowAddCreditModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCredit}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingCredit ? 'Update Credit' : 'Add Credit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FundingPage;
